import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { ILanguage } from "../../../models/Interfaces";
import { useConfirm } from "../../../config/ConfirmProvider";
import { CellProps, Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Button, Col, Row } from "react-bootstrap";
import { LanguageService } from "../../../services/LanguageService";
import Loading from "../../components/common/Loading";
import FilteredTable from "../../components/common/FilteredTable";
import AddUpdateLanguageModal from "./AddUpdateLanguage";
import AppEmptyState from "../../components/common/AppEmptyState";
import styles from "./siteLanguages.module.scss";


const SiteLanguages = () => {
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<ILanguage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);

  const languageSvcRef = useRef<LanguageService | null>(null);
  const confirmation = useConfirm();
  
  const columns: Column<ILanguage>[] = useMemo(() => [
    { 
      Header: "Language",
      accessor: "Language" 
    },
    { 
      Header: "Short Code", 
      accessor: "ShortCode",
      class: "text-bold"
    },
    {
      Header: "Enabled",
      Cell: ({ row }: CellProps<ILanguage>) => {
        return (
          <button
            type="button"
            className={styles.statusCell}
            onClick={() => toggleEnabled(row.original)}
            title={row.original.Enabled ? "Disable language" : "Enable language"}>
            <span className={row.original.Enabled ? styles.statusBadgeOn : styles.statusBadgeOff}>
              {row.original.Enabled ? "Enabled" : "Disabled"}
            </span>
          </button>
        );
      }
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<ILanguage>) => (
        <div className="user-result-actions user-result-actions--single">
          <Button variant="light" onClick={() => editLanguage(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="Edit language">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteLanguage(row.original)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete language">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [languages]);

  const getLanguages = async () : Promise<void> => {
    if (languageSvcRef.current) {
      const langs = await languageSvcRef.current.getSiteLanguages();
      if (langs) {
        setLanguages(langs);
      }
    }
  };

  const addLanguage = () : void => {
    if (languageSvcRef.current) {
      const newlang = languageSvcRef.current.newLanguage();
      if (newlang) {
        setSelectedLanguage(newlang);
        setShowModal(true);
      }
    }
  };

  const editLanguage = (selected: ILanguage) : void => {
    if (selected) {
      setSelectedLanguage(selected);
      setShowModal(true);
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedLanguage, (item) => item.Id, () => closeDetails());

  const deleteLanguage = async (selected: ILanguage) : Promise<void> => {
    if (selected && languageSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation(`Delete <b>${selected.Language}</b> from your site configuration? <b>This action cannot be undone</b>`, `Delete ${selected.Language}`);
      if (confirmed) {
        const res = await languageSvcRef.current.deleteLanguage(selected.Id);

        if (res) {
          setLanguages((prev) => {
            return prev.filter(l => l.Id !== selected.Id);
          });
          closeDeletedSelection(selected.Id);
        }
      }
    }
  };

  const saveLanguage = async (language: ILanguage) => {
    if (language && languageSvcRef.current) {
      const res = await languageSvcRef.current.saveLanguage(language);

      if (res) {
        const existing = languages.find(l => l.Id === res.Id);

        if (existing) {
          setLanguages((prev) => {
            return prev.map(l => l.Id === res.Id ? res : l);
          })
        } else {
          setLanguages(prev => ([
            ...prev,
            res
          ]));
        }
      }
      closeDetails();
    }
  };

  const toggleEnabled = async (language: ILanguage) : Promise<void> => {
    if (languageSvcRef.current && language) {
      language.Enabled = !language.Enabled;
      const res = await languageSvcRef.current.saveLanguage(language);
      if (res) {
        setLanguages((prev) => {
          return prev.map(lng => lng.Id === res.Id ? res : lng);
        });
      }
    }
  };

  const closeDetails = () : void => {
    setSelectedLanguage(null);
    setShowModal(false);
  }

  useEffect(() => {
    if (!languageSvcRef.current) {
      languageSvcRef.current = new LanguageService();
    }

    const initData = async () => {
      try {
        await Promise.all([
          getLanguages()
        ]);

        setLoading(false);

      } catch (error) {
        console.log(error);
      }
    }
    initData();
  }, []);

  return (
    <>
      {loading && (
        <Row>
          <Col md={12} className="text-center mt-5">
            <Loading Message="loading available site languages..." SpinSize="5x" key={"LoadingSpinner"}></Loading>
          </Col>
        </Row>
      )}

      {!loading && languages?.length > 0 && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div className={styles.sectionLead}>
                <div>
                  <h2 className="app-list-section__title">Language Library</h2>
                  <p className="app-list-section__summary">
                    Review, add, and maintain the languages available to this site, including their browser region codes and active status.
                  </p>
                </div>
                <div className={styles.headerActions}>
                  <Button variant="primary" onClick={addLanguage}>
                    <FontAwesomeIcon icon={faPlus} />
                    Add Language
                  </Button>
                </div>
              </div>
            </div>
            <div className="app-list-section__body">
              <FilteredTable<ILanguage>
                columns={columns}
                data={languages}
                noDataMessage="No site languages found, click the add button above to add a new language specification"
                showAddButton={true}
                addButtonText="Add Language"
                addButtonIcon={faPlus}
                addButtonHandler={addLanguage}
                cellClass="align-middle"
                filterPlaceHolder="filter languages..."
              />
            </div>
          </section>
        </div>
      )}

      {!loading && languages?.length === 0 && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div className={styles.sectionLead}>
                <div>
                  <h2 className="app-list-section__title">Language Library</h2>
                  <p className="app-list-section__summary">
                    Review, add, and maintain the languages available to this site, including their browser region codes and active status.
                  </p>
                </div>
                <div className={styles.headerActions}>
                  <Button variant="primary" onClick={addLanguage}>
                    <FontAwesomeIcon icon={faPlus} />
                    Add Language
                  </Button>
                </div>
              </div>
            </div>
            <div className="app-list-section__body">
              <div className={styles.emptyWrap}>
                <AppEmptyState
                  title="No site languages found"
                  message="Add your first site language to define the language options available across pages, menus, and site content."
                />
                <div className={styles.emptyActions}>
                  <Button
                    type="button"
                    variant="primary"
                    className="app-table__add-button"
                    onClick={addLanguage}>
                    <FontAwesomeIcon icon={faPlus} /> Add a site language
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      {!loading && selectedLanguage && showModal && (
        <AddUpdateLanguageModal
          language={selectedLanguage}
          onClose={closeDetails}
          onSave={(lang) => saveLanguage(lang)}
          key={"addUpdateHeaderModal"}
        />
      )}
    </>
  );
}

export default SiteLanguages;
